/** AI provider settings tab — sortable configured providers + unconfigured list + usage */

import { useState } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Sparkles } from 'lucide-react'
import { useAISettings, useUpdateAISetting, useDeleteAISetting, useAIUsage, useReorderAISettings } from '../../hooks/use-ai-settings'
import { AI_PROVIDERS } from '@agentwiki/shared'
import type { AIProviderId, AIProviderSetting, AIUsageRecord } from '@agentwiki/shared'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { SortableProviderRow } from './ai-sortable-provider-row'
import { SettingsSectionHeader } from './settings-section-header'
import { buttonPrimaryClass, buttonSecondaryClass, cardClass, inputClass, labelClass } from '../ui/styles'

const providerIds = Object.keys(AI_PROVIDERS) as AIProviderId[]

const groupHeadingClass = 'text-xs font-semibold text-fg-subtle'

export function AISettingsTab() {
  const t = useT()
  const { data } = useAISettings()
  const { data: usageData } = useAIUsage()
  const updateSetting = useUpdateAISetting()
  const deleteSetting = useDeleteAISetting()
  const reorderMutation = useReorderAISettings()

  const settings: AIProviderSetting[] = data?.settings ?? []
  const usage = usageData?.usage ?? []

  const configured = [...settings].sort((a, b) => a.priority - b.priority)
  const configuredIds = new Set(configured.map((s) => s.providerId))
  const unconfigured = providerIds.filter((id) => !configuredIds.has(id))

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = configured.findIndex((s) => s.providerId === active.id)
    const newIndex = configured.findIndex((s) => s.providerId === over.id)
    const reordered = arrayMove(configured, oldIndex, newIndex)
    reorderMutation.mutate(reordered.map((s, i) => ({ providerId: s.providerId, priority: i })))
  }

  return (
    <div className="space-y-6">
      <SettingsSectionHeader title={t('settings.ai.title')} description={t('settings.ai.subtitle')} />

      {configured.length > 0 && (
        <section className="space-y-2">
          <h3 className={groupHeadingClass}>{t('settings.ai.configured', { count: configured.length })}</h3>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={configured.map((s) => s.providerId)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {configured.map((setting) => {
                  const provider = AI_PROVIDERS[setting.providerId]
                  return (
                    <SortableProviderRow
                      key={setting.providerId}
                      setting={setting}
                      providerName={provider?.name ?? setting.providerId}
                      models={provider ? (provider.models as unknown as string[]) : [setting.defaultModel]}
                      onSave={(apiKey, model, enabled) =>
                        updateSetting.mutate({ providerId: setting.providerId, apiKey, defaultModel: model, isEnabled: enabled })
                      }
                      onDelete={() => deleteSetting.mutate(setting.providerId)}
                      isSaving={updateSetting.isPending}
                    />
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        </section>
      )}

      {unconfigured.length > 0 && (
        <section className="space-y-2">
          <h3 className={groupHeadingClass}>{t('settings.ai.available', { count: unconfigured.length })}</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {unconfigured.map((id) => (
              <UnconfiguredProviderCard
                key={id}
                providerId={id}
                providerName={AI_PROVIDERS[id].name}
                models={AI_PROVIDERS[id].models as unknown as string[]}
                onSave={(apiKey, model, enabled) => updateSetting.mutate({ providerId: id, apiKey, defaultModel: model, isEnabled: enabled })}
                isSaving={updateSetting.isPending}
              />
            ))}
          </div>
        </section>
      )}

      <UsageTable usage={usage} />
    </div>
  )
}

function UnconfiguredProviderCard({
  providerId,
  providerName,
  models,
  onSave,
  isSaving,
}: {
  providerId: string
  providerName: string
  models: string[]
  onSave: (apiKey: string, model: string, enabled: boolean) => void
  isSaving: boolean
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(models[0])
  const idBase = `new-provider-${providerId}`

  return (
    <div className={cn(cardClass, 'space-y-3 p-3')}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-fg">
          <Sparkles className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
          <span className="truncate">{providerName}</span>
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className={cn(open ? buttonSecondaryClass : buttonPrimaryClass, 'shrink-0 whitespace-nowrap')}>
          {open ? t('common.cancel') : t('settings.ai.configure')}
        </button>
      </div>

      {open && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor={`${idBase}-key`} className={labelClass}>
              {t('settings.ai.apiKey')}
            </label>
            <input
              id={`${idBase}-key`}
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t('settings.ai.enterKey')}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor={`${idBase}-model`} className={labelClass}>
              {t('settings.ai.model')}
            </label>
            <select id={`${idBase}-model`} value={model} onChange={(e) => setModel(e.target.value)} className={inputClass}>
              {models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!apiKey) return
              onSave(apiKey, model, true)
              setApiKey('')
              setOpen(false)
            }}
            disabled={isSaving || !apiKey}
            className={cn(buttonPrimaryClass, 'w-full')}
          >
            {isSaving ? t('settings.saving') : t('settings.save')}
          </button>
        </div>
      )}
    </div>
  )
}

function UsageTable({ usage }: { usage: AIUsageRecord[] }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
  const numberFormat = new Intl.NumberFormat(locale)

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-fg">{t('settings.ai.usage')}</h3>
      {usage.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-subtle">{t('settings.ai.noUsage')}</p>
      ) : (
        <div className={cn(cardClass, 'overflow-x-auto')}>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-muted text-left text-xs text-fg-subtle">
                <th className="px-3 py-2 font-medium">{t('settings.ai.col.provider')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.ai.col.model')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.ai.col.action')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('settings.ai.col.tokens')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('settings.ai.col.date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {usage.slice(0, 50).map((u, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 text-fg">{u.providerId}</td>
                  <td className="px-3 py-2 font-mono text-xs text-fg-muted">{u.model}</td>
                  <td className="px-3 py-2 text-fg-muted">{u.action}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-fg-muted">{numberFormat.format(u.inputTokens + u.outputTokens)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-fg-subtle">{dateFormat.format(new Date(u.createdAt))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
